import { Injectable } from "@angular/core";
import { UntypedFormGroup } from "@angular/forms";

import { combineLatest, from, Observable, of, Subject } from "rxjs";
import { map, mapTo, shareReplay } from "rxjs/operators";

import {
    InstallationNoteService,
    Note,
    NoteBruger,
    NoteFormBuilder,
    NoteHenvendelsestype,
    NoteOmraade,
    NoteOpgavestyringStatus
} from "@e-forsyning/common/note";

import { FeatureNoterViewModel } from "../model/feature-noter.view-model";
import { FeatureNoterRepository } from "./feature-noter.repository";

@Injectable({ providedIn: "root" })
export class FeatureNoterSandbox {
    private omraader: NoteOmraade[];
    private henvendelsestyper: NoteHenvendelsestype[];
    private opgavestyringStatusser: NoteOpgavestyringStatus[];
    private brugere: NoteBruger[];
    private readonly progressSubject = new Subject<number>();
    public readonly progress$ = this.progressSubject.asObservable();

    constructor(public readonly noteService: InstallationNoteService, private readonly repository: FeatureNoterRepository) {}

    public hentNoter(aktivNoteId: number): Observable<FeatureNoterViewModel> {
        const defaultOmraadeId = this.noteService.hentDefaultOmraadeId();
        const noter$ = this.noteService.hentNoter();
        const omraader$ = this.noteService.hentOmraader();
        const henvendelsestyper$ = this.noteService.hentHenvendelsestyper();
        const opgavestyringStatusser$ = this.noteService.hentOpgavestyringStatusser();
        const brugere$ = this.noteService.hentBrugere();
        const indstillinger$ = this.repository.hentIndstillinger();
        const result = combineLatest([noter$, omraader$, henvendelsestyper$, opgavestyringStatusser$, brugere$, indstillinger$]).pipe(
            map(([noter, omraader, henvendelsestyper, opgavestyringStatusser, brugere, indstillinger]) => ({
                data: {
                    aktivNoteId,
                    noter,
                    omraader,
                    henvendelsestyper,
                    opgavestyringStatusser,
                    brugere,
                    defaultOmraadeId,
                    formArray: NoteFormBuilder.noterToFormArray(noter, omraader, henvendelsestyper, opgavestyringStatusser, brugere)
                },
                indstillinger
            })),
            shareReplay(1)
        );
        result.subscribe(({ data }) => {
            this.omraader = data.omraader;
            this.henvendelsestyper = data.henvendelsestyper;
            this.opgavestyringStatusser = data.opgavestyringStatusser;
            this.brugere = data.brugere;
        });
        return result;
    }

    public hentNote(formGroup: UntypedFormGroup): Observable<UntypedFormGroup> {
        if (formGroup.value.fullyLoaded) {
            return of(formGroup);
        }

        const noten = NoteFormBuilder.formGroupToNote(formGroup);
        return this.noteService.hentNote(noten).pipe(map((note: Note) => this.noteToFormGroup(note)));
    }

    public gemNote(formGroup: UntypedFormGroup): Observable<UntypedFormGroup> {
        const note = NoteFormBuilder.formGroupToNote(formGroup);
        return from(
            this.noteService.gemNote(note, (progress) => {
                this.progressSubject.next(progress);
            })
        ).pipe(map((gemtNote: Note) => this.noteToFormGroup(gemtNote)));
    }

    public sletNote(formGroup: UntypedFormGroup): Observable<UntypedFormGroup> {
        const note = NoteFormBuilder.formGroupToNote(formGroup);
        // Returner 'null' for at indikere, at noten kan fjernes fra listen.
        // Vi kunne have returneret den oprindelige formGroup for at fortælle at den skal bibeholdes i listen med status=Slettet.
        // Dette gøres f.eks. i Servicerapport, hvor vi sletter in-memory og først sletter fysisk når hele rapporten udføres.
        return from(this.noteService.sletNote(note)).pipe(mapTo(null));
    }

    public noteToFormGroup(note: Note): UntypedFormGroup {
        return NoteFormBuilder.noteToFormGroup(note, this.omraader, this.henvendelsestyper, this.opgavestyringStatusser, this.brugere);
    }
}
